/**
 * HTML → PDF/image assembly (client-side).
 *
 * The heavy lifting is split across three modules now:
 *   - export-capture.ts  owns the hidden capture iframe (a CaptureSession) and
 *                        shared helpers (bg info, text runs, resource waits).
 *   - rasterizer/        turns the captured DOM into per-page canvas regions
 *                        (snapdom primary, html2canvas-pro fallback) — no more
 *                        single full-height canvas, so tall docs don't hit the
 *                        browser canvas caps.
 *   - this file          assembles the rastered regions into a PDF (jsPDF) or a
 *                        single image, adds the selectable text overlay, and
 *                        loads on-demand CJK/Arabic/etc. fonts.
 *
 * All page geometry is in CSS px (text runs are already CSS px); `scale` maps
 * to device px only at the moment a region is drawn.
 */
import jsPDF from 'jspdf'
import type { PdfExportOptions } from '../ui/PdfExportDialog.js'
import {
  PAGE_DIMS, hexToRgb,
  type CaptureSession, type TextRun,
} from './export-capture.js'
import { rasterize, type RasterSource } from './rasterizer/index.js'
import { computePageBreaks, parsePageRange } from './export-pagination.js'
import { ExportFault, reportExportError } from './export-errors.js'
import { imageRenderScale, pageRenderScale, checkExportFeasibility } from './export-feasibility.js'

// ─── Multi-script font system ──────────────────────────────────────
// Noto fonts loaded on-demand per script. Only fonts needed for the
// content are fetched. Cached in module scope + the Cache API after first load.
const GSTATIC = 'https://fonts.gstatic.com/s'
interface ScriptFont {
  name: string
  url: string
  covers: (c: number) => boolean
}

const SCRIPT_FONTS: ScriptFont[] = [
  { name: 'NotoSansSC', url: `${GSTATIC}/notosanssc/v40/k3kCo84MPvpLmixcA63oeAL7Iqp5IZJF9bmaG9_FnYw.ttf`,
    covers: c => (c >= 0x2E80 && c <= 0x9FFF) || (c >= 0x3000 && c <= 0x30FF) || (c >= 0xF900 && c <= 0xFAFF) },
  { name: 'NotoSansKR', url: `${GSTATIC}/notosanskr/v39/PbyxFmXiEBPT4ITbgNA5Cgms3VYcOA-vvnIzzuoyeLQ.ttf`,
    covers: c => (c >= 0xAC00 && c <= 0xD7AF) || (c >= 0x1100 && c <= 0x11FF) },
  { name: 'NotoSans', url: `${GSTATIC}/notosans/v42/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyD9A99d.ttf`,
    covers: c => (c >= 0x0370 && c <= 0x03FF) || (c >= 0x0400 && c <= 0x052F) },  // Greek + Cyrillic
  { name: 'NotoSansArabic', url: `${GSTATIC}/notosansarabic/v33/nwpxtLGrOAZMl5nJ_wfgRg3DrWFZWsnVBJ_sS6tlqHHFlhQ5l3sQWIHPqzCfyGyvuw.ttf`,
    covers: c => (c >= 0x0600 && c <= 0x06FF) || (c >= 0x0750 && c <= 0x077F) || (c >= 0xFB50 && c <= 0xFEFF) },
  { name: 'NotoSansDevanagari', url: `${GSTATIC}/notosansdevanagari/v30/TuGoUUFzXI5FBtUq5a8bjKYTZjtRU6Sgv3NaV_SNmI0b8QQCQmHn6B2OHjbL_08AlXQly-A.ttf`,
    covers: c => (c >= 0x0900 && c <= 0x097F) || (c >= 0xA8E0 && c <= 0xA8FF) },
  { name: 'NotoSansThai', url: `${GSTATIC}/notosansthai/v29/iJWnBXeUZi_OHPqn4wq6hQ2_hbJ1xyN9wd43SofNWcd1MKVQt_So_9CdU5RtpzE.ttf`,
    covers: c => c >= 0x0E00 && c <= 0x0E7F },
]

// L1 session cache: font name → binary string.
const fontCache = new Map<string, string>()
const FONT_CACHE_NAME = 'papan-export-fonts-v1'

function bytesToBinary(bytes: Uint8Array): string {
  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)))
  }
  return binary
}

/** A font download that delivers NO bytes for this long is abandoned. The
 *  export awaits the fonts before it builds the PDF, so an unbounded fetch
 *  turned a stalled CDN into a Download that never finished (spinner forever);
 *  abandoning it takes the documented degraded path instead (Latin overlay
 *  metrics + a `font-fetch` warn). An IDLE bound, not a total one: a slow but
 *  progressing multi-MB download is never cut off. */
export const FONT_STALL_MS = 20_000

/** `fetch` + full body read, aborted once no bytes arrive for `stallMs`. */
export async function fetchWithStallTimeout(url: string, name: string, stallMs = FONT_STALL_MS, fetchImpl: typeof fetch = fetch): Promise<ArrayBuffer> {
  const ctl = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const arm = () => { clearTimeout(timer); timer = setTimeout(() => ctl.abort(), stallMs) }
  try {
    arm()
    const res = await fetchImpl(url, { signal: ctl.signal })
    if (!res.ok) throw new Error(`Failed to fetch ${name}: ${res.status}`)
    if (!res.body) return await res.arrayBuffer()
    const reader = res.body.getReader()
    const chunks: Uint8Array[] = []
    let total = 0
    for (;;) {
      arm()
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      total += value.byteLength
    }
    const out = new Uint8Array(total)
    let off = 0
    for (const c of chunks) { out.set(c, off); off += c.byteLength }
    return out.buffer
  } catch (err) {
    if (ctl.signal.aborted) throw new Error(`Failed to fetch ${name}: no data for ${Math.round(stallMs / 1000)}s`)
    throw err
  } finally {
    clearTimeout(timer)
  }
}

/** Fetch a font and return as binary string (L1 module cache + L2 Cache API). */
async function fetchFont(font: ScriptFont): Promise<string> {
  const cached = fontCache.get(font.name)
  if (cached) return cached

  // L2: Cache API (survives reloads; the first CJK export in a session is the
  // only slow one). Best-effort — unavailable in insecure contexts.
  let cacheStore: Cache | null = null
  try {
    if (typeof caches !== 'undefined') cacheStore = await caches.open(FONT_CACHE_NAME)
  } catch { cacheStore = null }

  if (cacheStore) {
    try {
      const hit = await cacheStore.match(font.url)
      if (hit) {
        const binary = bytesToBinary(new Uint8Array(await hit.arrayBuffer()))
        fontCache.set(font.name, binary)
        return binary
      }
    } catch { /* fall through to network */ }
  }

  const buf = await fetchWithStallTimeout(font.url, font.name)
  if (cacheStore) {
    try { await cacheStore.put(font.url, new Response(buf, { headers: { 'content-type': 'font/ttf' } })) } catch { /* ignore */ }
  }
  const binary = bytesToBinary(new Uint8Array(buf))
  fontCache.set(font.name, binary)
  return binary
}

function detectNeededFonts(runs: { text: string }[]): ScriptFont[] {
  const needed = new Set<ScriptFont>()
  for (const run of runs) {
    for (let i = 0; i < run.text.length; i++) {
      const c = run.text.charCodeAt(i)
      if (c <= 0x024F) continue
      for (const font of SCRIPT_FONTS) {
        if (font.covers(c)) { needed.add(font); break }
      }
    }
  }
  return [...needed]
}

function fontForRun(text: string, loadedFonts: ScriptFont[]): string | null {
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    if (c <= 0x024F) continue
    for (const font of loadedFonts) {
      if (font.covers(c)) return font.name
    }
  }
  return null
}

/** Add invisible selectable text over a PDF page (rendering mode 3 = invisible).
 *  All inputs are CSS px; `pxToMm` converts to the PDF's mm space. `pageTopCss`
 *  is the content y-offset of this page's top; runs are placed relative to it. */
function addTextOverlay(
  pdf: jsPDF,
  runs: TextRun[],
  pageTopCss: number,
  pageBottomCss: number,
  pxToMm: number,
  offsetX: number,
  offsetY: number,
  loadedFonts?: ScriptFont[],
): void {
  const pageRuns = runs.filter((r) => {
    const mid = r.y + r.h / 2
    return mid >= pageTopCss && mid < pageBottomCss
  })
  if (pageRuns.length === 0) return

  ;(pdf as any).internal.write('3 Tr') // invisible but selectable

  for (const run of pageRuns) {
    const localY = run.y - pageTopCss
    const pdfX = run.x * pxToMm + offsetX
    // Baseline = half-leading + ascent = (h - fontSize)/2 + fontSize*0.8 = h/2 + fontSize*0.3
    const pdfY = (localY + run.h / 2 + run.fontSize * 0.3) * pxToMm + offsetY
    pdf.setFontSize(run.fontSize * pxToMm * 72 / 25.4)
    const runFont = loadedFonts?.length ? fontForRun(run.text, loadedFonts) : null
    pdf.setFont(runFont ?? 'helvetica')

    // Write CANONICAL logical Unicode (NFC), never presentation forms. The
    // overlay is invisible (3 Tr) — its only job is select/copy/search, and PDF
    // text extraction returns exactly the codepoints written here. Logical
    // Arabic (U+0600 block) is what users type into a search box and what apps
    // expect on paste; the previously-used arabic-reshaper mapped runs to
    // Presentation-Forms-B (U+FE70–FEFF) legacy codepoints, which broke search
    // and produced non-canonical copies. Visual glyph joining is irrelevant
    // here: the VISIBLE Arabic is the raster, already shaped by the browser.
    // NFC also recomposes decomposed diacritics (e.g. from contenteditable
    // edits) so extracted text matches what users search for.
    const text = run.text.normalize('NFC')
    const targetW = run.w * pxToMm
    const actualW = pdf.getTextWidth(text)
    const opts: any = {}
    if (text.length > 1 && actualW > 0) {
      opts.charSpace = (targetW - actualW) / text.length
    }
    pdf.text(text, pdfX, pdfY, opts)
  }

  ;(pdf as any).internal.write('0 Tr')
}

// ─── Encoding helpers ────────────────────────────────────────────────────

// PDF page images are ~99% of the PDF's bytes, so this constant IS the file
// size. 0.85 measured ~20% smaller than 0.92 at PSNR 47+ — indistinguishable
// at 2× zoom on text/gradient content (2026-07-14 devtools sweep).
const PDF_JPEG_QUALITY = 0.85
// Above ~0.95, canvas JPEG encoding inflates past lossless PNG while still
// degrading pixels — never worth it. Clamps user-persisted values too.
// Exported so the dialog's quality slider shares the same ceiling.
export const MAX_JPEG_QUALITY = 0.95

/** Async canvas encode → { bytes, format } for jsPDF.addImage (no main-thread
 *  toDataURL, no base64 inflation). PNG for small/gradient pages (no banding),
 *  JPEG otherwise. */
async function encodePageCanvas(canvas: HTMLCanvasElement, preferPng: boolean): Promise<{ bytes: Uint8Array; format: 'PNG' | 'JPEG' }> {
  const area = canvas.width * canvas.height
  const usePng = preferPng && area <= 4_000_000
  const mime = usePng ? 'image/png' : 'image/jpeg'
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mime, usePng ? undefined : PDF_JPEG_QUALITY))
  if (!blob) throw new ExportFault('Canvas toBlob failed (encode-fail)', 'encode-fail', 'assemble')
  return { bytes: new Uint8Array(await blob.arrayBuffer()), format: usePng ? 'PNG' : 'JPEG' }
}

// ─── Public API ──────────────────────────────────────────────────────────

export interface ExportHooks {
  onProgress?: (page: number, total: number) => void
}

export interface ExportResult {
  blob: Blob
  engine: RasterSource['engine']
  pages: number
}

/**
 * Render scale (CSS px → device px). The old fixed 2× made the output
 * resolution depend on the capture width — a narrow preview pane produced a
 * low-DPI export. Instead, target a fixed print DPI: the scale rises as the
 * capture width shrinks, so the output pixel dimensions (and memory) stay
 * roughly constant regardless of layout width. Clamped to [2, 4] so we never
 * downscale below retina crispness or blow past canvas limits.
 */
// Text and vector content re-renders sharp at the drawImage destination scale
// (the SVG-foreignObject is repainted by the browser, not upscaled as a bitmap),
// so raising the effective scale directly sharpens the export up to the point
// where embedded raster images hit their own source resolution. 300 DPI is
// print quality; higher yields diminishing returns and much larger files.
// pageRenderScale + imageRenderScale live in export-feasibility.js so the
// pre-flight check uses the exact same scale math as the render here.

/**
 * Generate an export blob from an already-settled CaptureSession. This is the
 * hot path the dialog calls for both Download and Save-to-Drive (the caller
 * caches the result so a second click doesn't re-render).
 */
export async function generateExportBlob(
  session: CaptureSession,
  options: PdfExportOptions,
  ctx: { fileId: string },
  hooks?: ExportHooks,
): Promise<ExportResult> {
  // The PREVIEW may render before slow assets finish (settle gate); the export
  // itself must not — wait for the full per-asset settle + remeasure.
  await session.settled

  const format = options.format ?? 'pdf'
  const pageSize = options.pageSize ?? 'a4'
  const orientation = options.orientation ?? 'portrait'
  const bgColorSetting = options.bgColor ?? 'auto'
  const bgFill = options.bgFill ?? 'extend'
  const fitToPage = options.fitToPage ?? false
  const jpegQuality = Math.min(MAX_JPEG_QUALITY, options.jpegQuality ?? 0.92)
  const bgAlpha = options.bgAlpha ?? 1

  const captureWidth = session.width()
  const contentHeight = session.contentHeight()
  const bg = session.bgInfo()
  const isImage = format === 'png' || format === 'jpeg'
  const isAuto = pageSize === 'auto'
  // Fit-Content derives the page height from the FULL-width content aspect, so
  // a nonzero margin would shrink the draw box to a different aspect ratio —
  // vertically stretching the raster and drifting the text overlay. The
  // margins UI is hidden for auto, but a persisted marginMm survives in the
  // options; auto means "the page is the content": force 0.
  const marginMm = isAuto ? 0 : Math.max(0, options.marginMm ?? 0)

  // Fail fast with a correctly-classified error if a single-canvas mode (image,
  // or PDF page "auto" / fit-to-page) would exceed the browser's dimension cap —
  // otherwise canvas.toBlob just returns null at the end (a less-specific
  // encode-fail). The dialog gates Download on this exact predicate; this also
  // covers Copy-as-PNG and any programmatic caller.
  const feas = checkExportFeasibility({ format, pageSize, orientation, fitToPage, marginMm, captureWidth, contentHeight })
  if (!feas.ok) {
    throw new ExportFault(
      `Export output ${feas.outputW}×${feas.outputH}px exceeds this browser's canvas limits (max dimension ${feas.maxDim}px)`,
      'canvas-limit', 'rasterize',
    )
  }

  const solidBg: [number, number, number] =
    bgColorSetting === 'auto' ? bg.color
    : bgColorSetting === 'transparent' ? bg.color
    : hexToRgb(bgColorSetting)

  const textRuns = isImage ? [] : session.collectTextRuns()

  // ── Image formats: one full-content raster + bg composite ──
  if (isImage) {
    const source = await rasterize({ el: session.doc.documentElement, widthCss: captureWidth, heightCss: contentHeight }, ctx)
    try {
      const content = await source.toCanvas({ x: 0, y: 0, w: captureWidth, h: contentHeight }, imageRenderScale(captureWidth))
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
      const needsBg = bgColorSetting !== 'transparent' || format === 'jpeg'
      let outputCanvas = content
      if (needsBg) {
        const alpha = format === 'png' ? bgAlpha : 1
        outputCanvas = document.createElement('canvas')
        outputCanvas.width = content.width
        outputCanvas.height = content.height
        const octx = outputCanvas.getContext('2d')!
        octx.fillStyle = `rgba(${solidBg[0]},${solidBg[1]},${solidBg[2]},${alpha})`
        octx.fillRect(0, 0, outputCanvas.width, outputCanvas.height)
        octx.drawImage(content, 0, 0)
      }
      const blob = await new Promise<Blob>((resolve, reject) =>
        outputCanvas.toBlob((b) => (b ? resolve(b) : reject(new ExportFault('Canvas toBlob failed (encode-fail)', 'encode-fail', 'assemble'))),
          mimeType, format === 'jpeg' ? jpegQuality : undefined))
      // Free the full-content backing stores promptly — these are the largest
      // canvases in the pipeline (same pattern as drawStrip below).
      if (outputCanvas !== content) outputCanvas.width = outputCanvas.height = 0
      content.width = content.height = 0
      return { blob, engine: source.engine, pages: 1 }
    } finally {
      source.dispose()
    }
  }

  // ── PDF ──
  const [rawW, rawH] = isAuto ? [210, 297] : PAGE_DIMS[pageSize]
  let pageMmW = orientation === 'portrait' ? rawW : rawH
  let pageMmH = orientation === 'portrait' ? rawH : rawW

  // Script fonts for the overlay: kick the (network) fetch off NOW but only
  // await it right before the fonts are registered — the multi-MB Noto
  // downloads then overlap the (CPU-bound) rasterization instead of running
  // back-to-back before it. The rasterizer never consumes these fonts.
  const neededFonts = textRuns.length > 0 ? detectNeededFonts(textRuns) : []
  const loadedFonts: ScriptFont[] = []
  const fontsFetch = neededFonts.length > 0 ? Promise.allSettled(neededFonts.map((f) => fetchFont(f))) : null
  let fontsReady: Promise<void> | null = null
  const awaitFonts = () => (fontsReady ??= (async () => {
    if (!fontsFetch) return
    const results = await fontsFetch
    results.forEach((r, i) => { if (r.status === 'fulfilled') loadedFonts.push(neededFonts[i]) })
    // A blocked/offline font CDN degrades the PDF text overlay (non-Latin text
    // falls back to helvetica metrics) but doesn't fail the export — record a
    // warn so the assistant can explain a "selectable CJK is off" report.
    const failed = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined
    if (failed) {
      reportExportError('fonts', failed.reason, { fileId: ctx.fileId, level: 'warn', dims: { contentHeight, captureWidth } })
    }
  })())

  const captureContent = () => rasterize({ el: session.doc.documentElement, widthCss: captureWidth, heightCss: contentHeight }, ctx)

  // ── auto / fit-to-page: single page ──
  if (isAuto || fitToPage) {
    if (isAuto) pageMmH = (contentHeight / captureWidth) * pageMmW
    const contentSource = await captureContent()
    try {
      await awaitFonts()
      const pdf = new jsPDF({ orientation: isAuto ? 'p' : (orientation === 'portrait' ? 'p' : 'l'), unit: 'mm', format: isAuto ? [pageMmW, pageMmH] : pageSize, compress: true })
      registerFonts(pdf, loadedFonts)
      const pdfW = pdf.internal.pageSize.getWidth()
      const pdfH = pdf.internal.pageSize.getHeight()
      const boxW = pdfW - 2 * marginMm
      const boxH = pdfH - 2 * marginMm
      const scale = pageRenderScale(captureWidth, boxW)

      const content = await contentSource.toCanvas({ x: 0, y: 0, w: captureWidth, h: contentHeight }, scale)
      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = Math.round(captureWidth * scale)
      pageCanvas.height = Math.round((captureWidth * boxH / boxW) * scale)
      const pctx = pageCanvas.getContext('2d')!
      pctx.fillStyle = `rgb(${solidBg[0]},${solidBg[1]},${solidBg[2]})`
      pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)

      let pxToMm = boxW / captureWidth
      let offX = marginMm, offY = marginMm
      if (isAuto) {
        pctx.drawImage(content, 0, 0, content.width, content.height, 0, 0, pageCanvas.width, pageCanvas.height)
      } else {
        // fit whole content into the content box, centered
        const contentAR = content.width / content.height
        const boxAR = pageCanvas.width / pageCanvas.height
        let dw: number, dh: number
        if (contentAR > boxAR) { dw = pageCanvas.width; dh = pageCanvas.width / contentAR }
        else { dh = pageCanvas.height; dw = pageCanvas.height * contentAR }
        pctx.drawImage(content, 0, 0, content.width, content.height, (pageCanvas.width - dw) / 2, (pageCanvas.height - dh) / 2, dw, dh)
        const fitW = (dw / pageCanvas.width) * boxW
        pxToMm = fitW / captureWidth
        offX = marginMm + (boxW - fitW) / 2
        offY = marginMm + (boxH - (dh / pageCanvas.height) * boxH) / 2
      }
      const enc = await encodePageCanvas(pageCanvas, bg.hasImage)
      content.width = content.height = 0
      pageCanvas.width = pageCanvas.height = 0
      pdf.addImage(enc.bytes, enc.format, marginMm, marginMm, boxW, boxH)
      addTextOverlay(pdf, textRuns, 0, contentHeight, pxToMm, offX, offY, loadedFonts)
      drawHeaderFooter(pdf, options, 1, 1, marginMm)
      hooks?.onProgress?.(1, 1)
      return { blob: pdf.output('blob'), engine: contentSource.engine, pages: 1 }
    } finally {
      contentSource.dispose()
    }
  }

  // ── multi-page: break-aware strips ──
  const pdf = new jsPDF({ orientation: orientation === 'portrait' ? 'p' : 'l', unit: 'mm', format: pageSize, compress: true })
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  const boxW = pdfW - 2 * marginMm
  const boxH = pdfH - 2 * marginMm
  const pxToMm = boxW / captureWidth
  // A content strip that fills the content box maps to this many CSS px.
  const pageContentCss = boxH / pxToMm
  const scale = pageRenderScale(captureWidth, boxW)

  const breakRects = session.collectBreakRects(textRuns)
  const breaks = computePageBreaks(breakRects, contentHeight, pageContentCss)
  const totalPages = breaks.length
  const selected = options.pageRange ? parsePageRange(options.pageRange, totalPages) : null

  // Optional background layer (tile/extend): one raster spanning the physical
  // page area, sliced per page. Captured with the bg PRESENT (before any strip).
  // Engines capture at different moments (snapdom eagerly at rasterize();
  // html2canvas-pro lazily at each toCanvas()), so the layer must STAY MOUNTED
  // until the last bg strip is drawn — removing it here made the fallback
  // engine rasterize a detached element (blank/thrown bg strips). It is hidden
  // between bg draws so a lazy engine doesn't bake it into the CONTENT strips,
  // and removed in the outer finally.
  let bgSource: RasterSource | null = null
  let bgLayer: { el: HTMLElement; remove: () => void } | null = null
  const needBgLayer = bg.hasImage && (bgFill === 'tile' || bgFill === 'extend')
  if (needBgLayer) {
    const totalBgCss = totalPages * pageContentCss
    bgLayer = await session.mountBgLayer(totalBgCss)
    try {
      bgSource = await rasterize({ el: bgLayer.el, widthCss: captureWidth, heightCss: totalBgCss }, ctx)
    } catch (e) {
      bgLayer.remove()
      bgLayer = null
      throw e
    }
    bgLayer.el.style.display = 'none'
  }

  const assemble = async (contentSource: RasterSource): Promise<ExportResult> => {
    await awaitFonts()
    registerFonts(pdf, loadedFonts)
    const drawStrip = async (top: number, bottom: number, physicalTop: number) => {
      const stripCss = bottom - top
      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = Math.round(captureWidth * scale)
      pageCanvas.height = Math.round(pageContentCss * scale)
      const pctx = pageCanvas.getContext('2d')!
      pctx.fillStyle = `rgb(${solidBg[0]},${solidBg[1]},${solidBg[2]})`
      pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
      if (bgSource) {
        if (bgLayer) bgLayer.el.style.display = ''
        const bgStrip = await bgSource.toCanvas({ x: 0, y: physicalTop, w: captureWidth, h: pageContentCss }, scale)
        if (bgLayer) bgLayer.el.style.display = 'none'
        pctx.drawImage(bgStrip, 0, 0)
        bgStrip.width = bgStrip.height = 0
      }
      const contentStrip = await contentSource.toCanvas({ x: 0, y: top, w: captureWidth, h: stripCss }, scale)
      pctx.drawImage(contentStrip, 0, 0)
      contentStrip.width = contentStrip.height = 0
      const enc = await encodePageCanvas(pageCanvas, bg.hasImage)
      pdf.addImage(enc.bytes, enc.format, marginMm, marginMm, boxW, boxH)
      pageCanvas.width = pageCanvas.height = 0
      addTextOverlay(pdf, textRuns, top, bottom, pxToMm, marginMm, marginMm, loadedFonts)
    }

    let emitted = 0
    let prev = 0
    const emittedPages = selected ? selected.length : totalPages
    for (let i = 0; i < totalPages; i++) {
      const top = prev
      const bottom = breaks[i]
      prev = bottom
      if (selected && !selected.includes(i + 1)) continue
      if (emitted > 0) pdf.addPage()
      await drawStrip(top, bottom, i * pageContentCss)
      emitted++
      drawHeaderFooter(pdf, options, emitted, emittedPages, marginMm)
      hooks?.onProgress?.(emitted, emittedPages)
      // Yield so the progress toast can paint and the UI stays responsive.
      await new Promise<void>((r) => setTimeout(r))
    }
    return { blob: pdf.output('blob'), engine: contentSource.engine, pages: emitted }
  }

  try {
    // For 'extend', the content is captured with its own background stripped so
    // the seamless bg layer shows through. The strip must be active during the
    // ACTUAL pixel capture, which differs by engine (snapdom captures at
    // rasterize(); html2canvas at each toCanvas()) — so hold it across the whole
    // capture + region-draw span, not just around a single toCanvas call.
    const runStripped = bgFill === 'extend' && bgSource
    const doAssemble = async () => {
      const contentSource = await captureContent()
      try {
        return await assemble(contentSource)
      } finally {
        contentSource.dispose()
      }
    }
    return runStripped ? await session.withBgStripped(doAssemble) : await doAssemble()
  } finally {
    bgSource?.dispose()
    bgLayer?.remove()
  }
}

function registerFonts(pdf: jsPDF, loadedFonts: ScriptFont[]): void {
  for (const font of loadedFonts) {
    const data = fontCache.get(font.name)
    if (!data) continue
    pdf.addFileToVFS(`${font.name}.ttf`, data)
    pdf.addFont(`${font.name}.ttf`, font.name, 'normal')
  }
}

/** Draw optional header/footer text (title / date / page numbers) in the margin
 *  band as real vector text. No-op when header/footer isn't requested or there's
 *  no margin to draw into. */
function drawHeaderFooter(pdf: jsPDF, options: PdfExportOptions, page: number, total: number, marginMm: number): void {
  const hf = options.headerFooter
  if (!hf || (!hf.title && !hf.date && !hf.pageNumbers)) return
  if (marginMm < 6) return // no room
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  pdf.setFont('helvetica')
  pdf.setFontSize(8)
  pdf.setTextColor(120)
  const y = pdfH - marginMm / 2
  if (hf.title && options.documentTitle) pdf.text(options.documentTitle.slice(0, 120), marginMm, marginMm / 2 + 2)
  if (hf.date) {
    const d = options.exportDateLabel ?? ''
    if (d) pdf.text(d, pdfW - marginMm, marginMm / 2 + 2, { align: 'right' })
  }
  if (hf.pageNumbers) pdf.text(`${page} / ${total}`, pdfW / 2, y, { align: 'center' })
  pdf.setTextColor(0)
}
